import React from 'react'

/**
 * Simple utility for retrying dynamic imports
 * Handles the "Failed to fetch dynamically imported module" error gracefully
 */

//Retries a dynamic import with simple backoff
const retryDynamicImport = async <T>(
  importFn: () => Promise<T>,
  maxRetries = 3
): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await importFn()
    } catch (error) {
      const isDynamicImportError =
        error instanceof Error &&
        (error.message.includes(
          'Failed to fetch dynamically imported module'
        ) ||
          error.message.includes('Loading chunk'))

      if (!isDynamicImportError || attempt === maxRetries) {
        throw error
      }

      const delay = (attempt + 1) * 1000
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  throw new Error('Max retries exceeded')
}

//Creates a React.lazy component with retry logic
export const createRetryLazy = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => {
  return React.lazy(() => retryDynamicImport(importFn))
}

// Global error handler for dynamic import failures
export const setupDynamicImportErrorHandler = () => {
  window.addEventListener('unhandledrejection', event => {
    const error = event.reason as Error

    if (
      error?.message?.includes('Failed to fetch dynamically imported module')
    ) {
      console.warn('Dynamic import failed, this is usually temporary')
      event.preventDefault()
    }
  })
}
